import React, { useState, useEffect } from "react";
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Upload,
  Plus,
} from "lucide-react";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/signO/dashboard/DashboardLayout";
import StatCard from "../../components/signO/dashboard/StatCard";
import DocumentList from "../../components/signO/dashboard/DocumentList";
import { getCurrentUser } from "../../services/auth";
import { fetchDocuments, getDocumentStats } from "../../services/api";

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    rejected: 0,
  });
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const userData = getCurrentUser();
        setUser(userData);

        // Mock data for now if API fails or returns empty
        // In a real scenario, we would await these calls
        // const statsData = await getDocumentStats();
        // const docsData = await fetchDocuments('all');

        // Simulating API response
        setTimeout(() => {
          setStats({
            total: 12,
            pending: 4,
            completed: 7,
            rejected: 1,
          });

          setRecentDocs([
            {
              id: 1,
              name: "NDA_Contract_v2.pdf",
              status: "pending",
              date: "2024-03-10",
              size: "2.4 MB",
            },
            {
              id: 2,
              name: "Offer_Letter_JohnDoe.pdf",
              status: "completed",
              date: "2024-03-08",
              size: "1.1 MB",
            },
            {
              id: 3,
              name: "Service_Agreement.pdf",
              status: "completed",
              date: "2024-03-05",
              size: "3.5 MB",
            },
            {
              id: 4,
              name: "Invoice_#1023.pdf",
              status: "rejected",
              date: "2024-03-01",
              size: "0.5 MB",
            },
            {
              id: 5,
              name: "Project_Proposal.pdf",
              status: "pending",
              date: "2024-02-28",
              size: "4.2 MB",
            },
          ]);
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error("Failed to load dashboard data", error);
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">
            Dashboard Overview
          </h2>
          <p className="text-gray-400">
            Welcome back, {user?.first_name || "User"}! Here's what's happening
            today.
          </p>
        </div>
        <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all shadow-lg shadow-primary/20 hover:scale-105">
          <Upload className="w-5 h-5" />
          <span>Upload Document</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard
          title="Total Documents"
          value={stats.total}
          icon={<FileText />}
          color="blue"
          trend={12}
        />
        <StatCard
          title="Pending Action"
          value={stats.pending}
          icon={<Clock />}
          color="yellow"
        />
        <StatCard
          title="Completed"
          value={stats.completed}
          icon={<CheckCircle />}
          color="green"
          trend={8}
        />
        <StatCard
          title="Rejected/Expired"
          value={stats.rejected}
          icon={<AlertCircle />}
          color="red"
          trend={-2}
        />
      </div>

      {/* Recent Documents */}
      <div className="mb-10">
        <DocumentList title="Recent Documents" documents={recentDocs} />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="glass-card p-6 rounded-2xl border border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center mb-4">
            <Plus className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Create Template</h3>
          <p className="text-sm text-gray-400">
            Save time by creating reusable templates for frequently used
            documents.
          </p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="glass-card p-6 rounded-2xl border border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-pink-500/20 text-pink-400 flex items-center justify-center mb-4">
            <Upload className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Bulk Send</h3>
          <p className="text-sm text-gray-400">
            Send the same document to multiple recipients at once for signing.
          </p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="glass-card p-6 rounded-2xl border border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Verify Document</h3>
          <p className="text-sm text-gray-400">
            Upload a signed document to verify its authenticity on the
            blockchain.
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
